if (!customElements.get('td-product-lineup')) {
  class TDProductLineup extends HTMLElement {
    static selectors = {
      letter: '[data-td-letter]',
      grid: '[data-td-lineup-grid]',
      item: '[data-td-lineup-item]',
      headline: '[data-td-headline]',
      details: '[data-td-lineup-details]',
    };

    // Reveal progress 0→1 while headline travels from start to end viewport ratio.
    static revealStartRatio = 0.95;
    static revealEndRatio = 0.62;

    constructor() {
      super();
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleItemActivate = this.handleItemActivate.bind(this);
      this.handleGridRelease = this.handleGridRelease.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.renderFrame = this.renderFrame.bind(this);
    }

    connectedCallback() {
      this.letters = Array.from(this.querySelectorAll(TDProductLineup.selectors.letter));
      this.items = Array.from(this.querySelectorAll(TDProductLineup.selectors.item));
      this.headline = this.querySelector(TDProductLineup.selectors.headline);
      this.squeezeQuery = window.matchMedia('(min-width: 990px)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.isTracking = false;
      this.renderFrameId = null;
      this.revealedCount = 0;
      this.sectionObserver = null;

      this.grid = this.querySelector(TDProductLineup.selectors.grid);

      this.items.forEach((item) => {
        item.addEventListener('mouseenter', this.handleItemActivate);
        item.addEventListener('focusin', this.handleItemActivate);
      });

      this.grid?.addEventListener('mouseleave', this.handleGridRelease);
      this.grid?.addEventListener('focusout', this.handleGridRelease);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      // Reserve details height up front so activation doesn't shift layout.
      this.lastViewportWidth = window.innerWidth;
      this.measureDetailsZones();
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.fonts?.ready?.then(() => {
        if (this.isConnected) {
          this.measureDetailsZones();
        }
      });

      if (this.isDesignMode || this.reduceMotionQuery.matches) {
        this.setRevealedCount(this.letters.length);
        return;
      }

      if (!this.letters.length || !this.headline) {
        return;
      }

      this.sectionObserver = new IntersectionObserver(this.handleIntersection, {
        root: this.getIntersectionRoot(),
        rootMargin: '20% 0px 20% 0px',
        threshold: 0,
      });
      this.sectionObserver.observe(this);
      this.updateLetterReveal();
    }

    disconnectedCallback() {
      this.stopTracking();
      this.items.forEach((item) => {
        item.removeEventListener('mouseenter', this.handleItemActivate);
        item.removeEventListener('focusin', this.handleItemActivate);
      });
      this.grid?.removeEventListener('mouseleave', this.handleGridRelease);
      this.grid?.removeEventListener('focusout', this.handleGridRelease);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('resize', this.handleResize);

      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
        this.sectionObserver = null;
      }
    }

    handleResize() {
      const width = window.innerWidth;
      if (width === this.lastViewportWidth) {
        return;
      }

      this.lastViewportWidth = width;
      this.measureDetailsZones();
    }

    measureDetailsZones() {
      this.items.forEach((item) => {
        const details = item.querySelector(TDProductLineup.selectors.details);
        if (details) {
          item.style.setProperty('--td-lineup-details-height', `${details.offsetHeight}px`);
        }
      });

    }

    getIntersectionRoot() {
      if (this.squeezeQuery.matches) {
        return document.querySelector('.page-wrapper') || null;
      }

      return null;
    }

    handleIntersection(entries) {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isIntersecting) {
        this.startTracking();
      } else {
        this.stopTracking();
      }
    }

    startTracking() {
      if (this.isTracking) {
        return;
      }

      this.isTracking = true;
      this.renderFrameId = requestAnimationFrame(this.renderFrame);
    }

    stopTracking() {
      this.isTracking = false;

      if (this.renderFrameId) {
        cancelAnimationFrame(this.renderFrameId);
        this.renderFrameId = null;
      }
    }

    renderFrame() {
      if (!this.isTracking || !this.isConnected) {
        this.renderFrameId = null;
        return;
      }

      this.updateLetterReveal();
      this.renderFrameId = requestAnimationFrame(this.renderFrame);
    }

    getRevealProgress() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const rect = this.headline.getBoundingClientRect();
      const start = viewportHeight * TDProductLineup.revealStartRatio;
      const end = viewportHeight * TDProductLineup.revealEndRatio;
      const travel = rect.height + (start - end);

      if (travel <= 0) {
        return 1;
      }

      return Math.min(Math.max((start - rect.top) / travel, 0), 1);
    }

    updateLetterReveal() {
      this.setRevealedCount(Math.round(this.getRevealProgress() * this.letters.length));
    }

    // Update only letters that crossed the reveal threshold since last frame.
    setRevealedCount(targetCount) {
      if (targetCount === this.revealedCount) {
        return;
      }

      const from = Math.min(this.revealedCount, targetCount);
      const to = Math.max(this.revealedCount, targetCount);
      for (let index = from; index < to; index += 1) {
        this.letters[index].dataset.tdRevealed = index < targetCount ? 'true' : 'false';
      }
      this.revealedCount = targetCount;
    }

    handleItemActivate(event) {
      this.setActiveItem(event.currentTarget.dataset.tdBlockId);
    }

    handleGridRelease(event) {
      if (event.type === 'focusout' && this.grid.contains(event.relatedTarget)) {
        return;
      }

      this.setActiveItem(null);
    }

    setActiveItem(blockId) {
      this.items.forEach((item) => {
        item.dataset.tdActive = item.dataset.tdBlockId === blockId ? 'true' : 'false';
      });
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) {
        return;
      }

      const blockId = event.detail?.blockId;
      if (blockId) {
        this.setActiveItem(blockId);
      }
    }
  }

  customElements.define('td-product-lineup', TDProductLineup);
}
